/**
 * Render Slack `rich_text` blocks to plain text / markdown (ENG-6175).
 *
 * Why this exists: when a user pastes a spreadsheet range (Excel, Google
 * Sheets, a web table) into Slack, Slack delivers the table as a
 * `rich_text_table` element inside the message's `blocks` and leaves the
 * event's top-level `text` field as ONLY the typed lead-in line. Slack's
 * own plaintext fallback (`evt.text`) does not flatten table cells. The
 * inbound handler historically forwarded only `evt.text`, so the table
 * rows were silently dropped and the agent reported it "couldn't read the
 * attachment / nothing landed". Slack's native message-table feature is the
 * second shape (ENG-7424): the table arrives as a TOP-LEVEL block of type
 * `table` (not nested inside `rich_text`), with `evt.text` empty when the
 * message is table-only.
 *
 * This module is pure and transport-free so it can be unit-tested without
 * the MCP server. It is defensive by construction: Slack does not publicly
 * document the `rich_text_table` shape, so the renderer walks the block
 * tree structurally, handles every documented `rich_text` element type,
 * renders any table-like element as a markdown table, and falls back to a
 * generic deep text-extraction when an element's shape is unrecognized.
 * Malformed / partial / unexpected input never throws — it degrades to the
 * best text it can recover (possibly empty).
 */

/** Max nesting we will recurse into — guards against a pathological/cyclic payload. */
const MAX_DEPTH = 12;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

/** True for any element whose `type` names a table (defensive — Slack's
 *  exact element name for pasted tables is undocumented and may evolve). */
function isTableElement(el: unknown): boolean {
  return isRecord(el) && asString(el.type).includes('table');
}

/** Convert a Slack emoji element to text. `unicode` is space- or hyphen-free
 *  hex codepoints (e.g. "1f600"); fall back to the `:shortcode:` form. */
function renderEmoji(el: Record<string, unknown>): string {
  const unicode = asString(el.unicode);
  if (unicode) {
    try {
      const cps = unicode.split('-').map((h) => parseInt(h, 16));
      if (cps.every((n) => Number.isFinite(n) && n >= 0 && n <= 0x10ffff)) {
        return String.fromCodePoint(...cps);
      }
    } catch {
      // fall through to shortcode
    }
  }
  const name = asString(el.name);
  return name ? `:${name}:` : '';
}

/**
 * Render an inline leaf element (the things inside a `rich_text_section`):
 * text, link, emoji, user/channel/usergroup mentions, broadcast.
 */
function renderLeaf(el: unknown): string {
  if (!isRecord(el)) return '';
  switch (asString(el.type)) {
    case 'text':
      return asString(el.text);
    case 'link': {
      const label = asString(el.text);
      const url = asString(el.url);
      if (label && url && label !== url) return `${label} (${url})`;
      return label || url;
    }
    case 'emoji':
      return renderEmoji(el);
    case 'user':
      return `<@${asString(el.user_id)}>`;
    case 'usergroup':
      return `<!subteam^${asString(el.usergroup_id)}>`;
    case 'channel':
      return `<#${asString(el.channel_id)}>`;
    case 'broadcast':
      return `@${asString(el.range) || 'channel'}`;
    case 'date':
      return asString(el.fallback) || asString(el.timestamp);
    default:
      // Unknown leaf — if it nests further elements, descend; else try `text`.
      if (Array.isArray(el.elements)) return renderInline(el.elements);
      return asString(el.text);
  }
}

/** Render an array of inline leaves to a single string. */
function renderInline(elements: unknown[]): string {
  return elements.map(renderLeaf).join('');
}

/**
 * Generic deep text extraction for an element of unknown shape: collect
 * every `text` we can find, joining leaves with no separator and nested
 * containers with the supplied joiner. Used as the table-cell / unknown
 * fallback so we never silently drop recoverable content.
 */
function deepText(node: unknown, depth: number): string {
  if (depth > MAX_DEPTH) return '';
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map((n) => deepText(n, depth + 1)).join('');
  if (!isRecord(node)) return '';
  const type = asString(node.type);
  if (type && type !== 'rich_text' && !type.startsWith('rich_text_') && !type.includes('table')) {
    // It's a leaf-ish element (text/link/emoji/mention/...) — use the leaf renderer.
    return renderLeaf(node);
  }
  if (Array.isArray(node.elements)) return node.elements.map((n) => deepText(n, depth + 1)).join('');
  if (Array.isArray(node.rows)) return node.rows.map((n) => deepText(n, depth + 1)).join(' ');
  return asString(node.text);
}

/** Render a single table cell's content to a one-line string. */
function renderCell(cell: unknown): string {
  if (!isRecord(cell)) return deepText(cell, 0).replace(/\s*\n\s*/g, ' ').trim();
  // A cell is typically a rich_text_section-like container with `elements`.
  const inner = Array.isArray(cell.elements)
    ? renderSection(cell, 0)
    : deepText(cell, 0);
  return inner.replace(/\s*\n\s*/g, ' ').trim();
}

/**
 * Render a table-like element to a markdown table. Tries the nested
 * `elements` (rows) → `elements`/`cells` (cells) shape first; falls back to
 * generic deep extraction if the structure is unrecognized.
 */
function renderTable(el: Record<string, unknown>): string {
  const rawRows = asArray(el.elements).length ? asArray(el.elements) : asArray(el.rows);
  const rows: string[][] = [];
  for (const row of rawRows) {
    if (!isRecord(row) && !Array.isArray(row)) continue;
    const rawCells = Array.isArray(row)
      ? row
      : asArray((row as Record<string, unknown>).elements).length
        ? asArray((row as Record<string, unknown>).elements)
        : asArray((row as Record<string, unknown>).cells);
    if (rawCells.length === 0) continue;
    rows.push(rawCells.map(renderCell));
  }

  if (rows.length === 0) {
    // Unrecognized shape — recover whatever text we can rather than drop it.
    const fallback = deepText(el, 0).replace(/[ \t]+\n/g, '\n').trim();
    return fallback;
  }

  const colCount = rows.reduce((max, r) => Math.max(max, r.length), 0);
  const pad = (r: string[]) => {
    const cells = r.slice();
    while (cells.length < colCount) cells.push('');
    // Escape pipes so a cell value can't break the markdown table grid.
    return cells.map((c) => c.replace(/\|/g, '\\|'));
  };

  const lines: string[] = [];
  const header = rows[0] ?? [];
  const body = rows.slice(1);
  lines.push(`| ${pad(header).join(' | ')} |`);
  lines.push(`| ${Array.from({ length: colCount }, () => '---').join(' | ')} |`);
  for (const r of body) lines.push(`| ${pad(r).join(' | ')} |`);
  return lines.join('\n');
}

/**
 * Render a section-level element (the direct children of a `rich_text`
 * block): section, list, preformatted, quote, or table.
 */
function renderSection(section: unknown, depth: number): string {
  if (depth > MAX_DEPTH || !isRecord(section)) return '';
  const type = asString(section.type);

  if (isTableElement(section)) return renderTable(section);

  switch (type) {
    case 'rich_text_section':
      return renderInline(asArray(section.elements));
    case 'rich_text_preformatted': {
      const code = renderInline(asArray(section.elements));
      return code ? `\`\`\`\n${code}\n\`\`\`` : '';
    }
    case 'rich_text_quote': {
      const quoted = renderInline(asArray(section.elements));
      return quoted
        ? quoted
            .split('\n')
            .map((l) => `> ${l}`)
            .join('\n')
        : '';
    }
    case 'rich_text_list': {
      const ordered = asString(section.style) === 'ordered';
      const items = asArray(section.elements).map((item, i) => {
        const body = renderSection(item, depth + 1) || renderInline(asArray((item as Record<string, unknown>)?.elements));
        const marker = ordered ? `${i + 1}.` : '-';
        return `${marker} ${body}`.trimEnd();
      });
      return items.join('\n');
    }
    default:
      // Unknown section type — descend if it has elements, else deep-extract.
      if (Array.isArray(section.elements)) return renderInline(asArray(section.elements));
      return deepText(section, depth + 1);
  }
}

export interface RenderedRichText {
  /** Rendered text/markdown for the whole block tree. May be empty. */
  text: string;
  /** True when at least one table-like element was found and rendered. */
  hadTable: boolean;
}

/**
 * Render Slack `blocks` (the `evt.blocks` array) to text/markdown.
 *
 * Two block shapes carry user-authored message content:
 *  - `rich_text` blocks (typed text, plus the `rich_text_table` element a
 *    spreadsheet paste produces, ENG-6175)
 *  - top-level `table` blocks - Slack's native message-table feature delivers
 *    the table as its own block (rows of cells), NOT nested inside a
 *    `rich_text` block, and leaves `evt.text` empty for a table-only message
 *    (ENG-7424)
 * Other block types (sections with accessories, dividers, images, etc.) are
 * skipped - those are app-authored Block Kit, already represented elsewhere,
 * and not the gap this addresses.
 *
 * Never throws: any malformed sub-tree degrades to the best text recoverable.
 */
export function renderSlackBlocks(blocks: unknown): RenderedRichText {
  let hadTable = false;
  const out: string[] = [];
  try {
    for (const block of asArray(blocks)) {
      if (!isRecord(block)) continue;
      if (isTableElement(block)) {
        hadTable = true;
        const rendered = renderTable(block);
        if (rendered.trim().length > 0) out.push(rendered);
        continue;
      }
      if (asString(block.type) !== 'rich_text') continue;
      for (const section of asArray(block.elements)) {
        if (isTableElement(section)) hadTable = true;
        const rendered = renderSection(section, 0);
        if (rendered.trim().length > 0) out.push(rendered);
      }
    }
  } catch {
    // Defensive: a renderer bug must never drop the inbound. Return whatever
    // we accumulated before the throw.
  }
  // Collapse 3+ blank lines, trim trailing whitespace per line.
  const text = out
    .join('\n\n')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { text, hadTable };
}

/**
 * Merge the event's plain `text` with rendered block content for the inbound
 * notification body. Behaviour is deliberately conservative:
 *
 *  - If the blocks contain NO table, return `text` unchanged. Normal
 *    messages already have a faithful `evt.text`; re-deriving them from
 *    blocks risks subtle formatting regressions for zero benefit.
 *  - If the blocks DO contain a table, prefer the fully-rendered block text
 *    (lead-in line + table) since `evt.text` is missing the table. If the
 *    rendered text somehow came back empty, fall back to `text` so we never
 *    blank out a message.
 *
 * Returns the body to forward plus whether a table was recovered (for logging).
 */
export function mergeInboundText(text: string, blocks: unknown): { content: string; recoveredTable: boolean } {
  const { text: rendered, hadTable } = renderSlackBlocks(blocks);
  if (!hadTable) return { content: text, recoveredTable: false };
  const content = rendered.length > 0 ? rendered : text;
  return { content, recoveredTable: rendered.length > 0 && content !== text };
}
